import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Kioo Radio live stream — proxied over HTTPS by our backend so it plays
// on the production site without mixed-content blocking.
// Upstream: http://xstreamer.galcom.org:8000/KIOO
const RADIO_STREAM_URL = `${BACKEND_URL}/api/stream/kioo`;

const ListenLive = () => {
  const [lowDataMode, setLowDataMode] = useState(false);
  const [upcomingPrograms, setUpcomingPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [useIframe, setUseIframe] = useState(false);
  const audioRef = useRef(null);

  const currentStreamUrl = RADIO_STREAM_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const programsRes = await axios.get(`${API}/programs`);
        setUpcomingPrograms(programsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching radio data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (useIframe || !audioRef.current) return;

    const audio = audioRef.current;

    const handleLoadStart = () => {
      setIsLoading(true);
      setStreamError(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setStreamError(false);
    };

    const handleError = (e) => {
      console.error('Audio error on ListenLive:', e);
      setIsLoading(false);
      setStreamError(true);
      setIsPlaying(false);
      
      // Try iframe approach if audio fails
      setUseIframe(true);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
      setStreamError(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [useIframe]);

  const handlePlayPause = async () => {
    if (isLoading) return;
    
    // For external radio streams, directly open in new tab for best compatibility
    if (!isPlaying) {
      setIsLoading(true);
      setStreamError(false);
      
      try {
        // First try HTML5 audio
        if (audioRef.current) {
          audioRef.current.load();
          const playPromise = audioRef.current.play();
          
          if (playPromise !== undefined) {
            playPromise.then(() => {
              setIsPlaying(true);
              setIsLoading(false);
              setStreamError(false);
            }).catch(async (error) => {
              console.error('HTML5 audio failed:', error);
              
              // If HTML5 fails, immediately use iframe
              setUseIframe(true);
              setIsPlaying(true);
              setIsLoading(false);
              setStreamError(false);
              
              // Also open external player as backup
              window.open(RADIO_STREAM_URL, '_blank', 'noopener,noreferrer');
            });
          }
        }
      } catch (e) {
        console.error('Stream connection failed:', e);
        
        // Direct fallback: Open external player
        window.open(RADIO_STREAM_URL, '_blank', 'noopener,noreferrer');
        setIsPlaying(true);
        setIsLoading(false);
        setStreamError(false);
      }
    } else {
      // Pause functionality
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
      setUseIframe(false);
    }
  };

  const forceIframe = () => {
    setUseIframe(true);
    setIsPlaying(true);
    setStreamError(false);
    setIsLoading(false);
  };

  // Enhanced stream testing function
  const testStreamConnection = async () => {
    if (!audioRef.current) return false;
    
    try {
      // Test if stream is reachable
      const testAudio = new Audio(RADIO_STREAM_URL);
      testAudio.volume = 0; // Mute for testing
      testAudio.preload = 'metadata';
      
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          testAudio.remove();
          resolve(false);
        }, 5000); // 5 second timeout
        
        testAudio.oncanplay = () => {
          clearTimeout(timeout);
          testAudio.remove();
          resolve(true);
        };
        
        testAudio.onerror = () => {
          clearTimeout(timeout);
          testAudio.remove();
          resolve(false);
        };
        
        testAudio.load();
      });
    } catch (e) {
      console.error('Stream test failed:', e);
      return false;
    }
  };

  const handleAudioError = () => {
    setStreamError(true);
    setIsPlaying(false);
  };

  const handleAudioLoad = () => {
    setStreamError(false);
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="radio-waves mb-4">
            <div className="w-16 h-16 border-4 border-kioo-primary rounded-full"></div>
          </div>
          <p className="text-kioo-primary">Connecting to Kioo Radio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-kioo-primary via-kioo-secondary to-kioo-dark">
      
      {/* Main Player Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Live Status Banner */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
              <span className="w-3 h-3 bg-red-400 rounded-full live-pulse"></span>
              <span className="text-white font-semibold">🔴 LIVE BROADCAST</span>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4">
              Kioo Radio 98.1 FM
            </h1>
            <p className="text-xl text-green-100 mb-8">
              The Gift of Good News • Broadcasting Live 24/7
            </p>
          </div>

          {/* Audio Player */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-white border-opacity-20">
            
            {/* Live Radio Info */}
            <div className="text-center mb-8">
              <p className="text-green-200 text-sm mb-2">Now Broadcasting</p>
              <h2 className="text-2xl font-bold text-white mb-2">Kioo Radio 98.1 FM</h2>
              <p className="text-green-100">The Gift of Good News</p>
              <div className="flex items-center justify-center space-x-4 mt-4 text-sm text-green-200">
                <span>📡 98.1 FM</span>
                <span>•</span>
                <span>🌍 Makona River Region</span>
              </div>
            </div>

            {/* Enhanced Audio Player */}
            <div className="flex flex-col items-center space-y-6">
              
              {/* Streaming Player with Controls */}
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-6 w-full max-w-md">
                
                {/* Modern Advanced Audio Player */}
                <div className="bg-gradient-to-br from-black/30 to-black/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                  {/* Hidden Audio Elements for Multiple Stream Attempts */}
                  <audio 
                    ref={audioRef}
                    preload="none"
                    onLoadStart={() => setIsLoading(true)}
                    onCanPlay={() => setIsLoading(false)}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onError={handleAudioError}
                    onAbort={() => setIsLoading(false)}
                    onSuspend={() => setIsLoading(false)}
                    crossOrigin="anonymous"
                    className="hidden"
                  >
                    <source src={RADIO_STREAM_URL} type="audio/mpeg" />
                    <source src={RADIO_STREAM_URL} type="audio/aac" />
                    <source src={RADIO_STREAM_URL} type="audio/mp3" />
                    Your browser does not support the audio element.
                  </audio>

                  {/* Player Header */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center space-x-3 bg-red-500/20 px-4 py-2 rounded-full border border-red-400/30">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-white text-sm font-semibold tracking-wide">LIVE BROADCAST</span>
                    </div>
                  </div>

                  {/* Main Player Interface */}
                  <div className="flex items-center justify-between">
                    {/* Station Info */}
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-kioo-primary to-kioo-secondary rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-white text-2xl font-bold">📻</span>
                      </div>
                      <div>
                        <h3 className="text-white text-xl font-bold">Kioo Radio</h3>
                        <p className="text-green-200 text-sm">98.1 FM • The Gift of Good News</p>
                        <p className="text-gray-300 text-xs">Makona River Region</p>
                      </div>
                    </div>

                    {/* Main Play Button */}
                    <div className="flex items-center space-x-6">
                      {/* Volume/Connection Indicator */}
                      <div className="flex items-center space-x-1">
                        {[1, 2, 3, 4].map((bar) => (
                          <div
                            key={bar}
                            className={`w-1 bg-green-400 rounded-full transition-all duration-300 ${
                              isPlaying 
                                ? `h-${2 + bar} animate-pulse` 
                                : 'h-2 opacity-50'
                            }`}
                            style={{
                              height: isPlaying ? `${4 + bar * 2}px` : '8px',
                              animationDelay: `${bar * 0.1}s`
                            }}
                          ></div>
                        ))}
                      </div>

                      {/* Play/Pause Button */}
                      <button
                        onClick={handlePlayPause}
                        disabled={isLoading}
                        className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 shadow-2xl ${
                          isPlaying 
                            ? 'bg-gradient-to-br from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 animate-pulse' 
                            : isLoading
                              ? 'bg-gradient-to-br from-yellow-500 to-orange-500 cursor-not-allowed'
                              : 'bg-gradient-to-br from-kioo-primary to-kioo-secondary hover:from-green-500 hover:to-green-600 hover:scale-110'
                        } disabled:opacity-70`}
                        title={isLoading ? 'Connecting...' : isPlaying ? 'Pause Live Stream' : 'Play Live Stream'}
                      >
                        <span className="text-white text-3xl">
                          {isLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Stream Status & Controls */}
                  <div className="mt-6 space-y-4">
                    {/* Status Bar */}
                    <div className="bg-black/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-2 h-2 rounded-full ${
                            streamError ? 'bg-red-500' : 
                            isLoading ? 'bg-yellow-500 animate-pulse' : 
                            isPlaying ? 'bg-green-500 animate-pulse' : 'bg-gray-500'
                          }`}></div>
                          <span className="text-white text-sm">
                            {streamError ? '⚠️ Connection Error - Retrying...' :
                             isLoading ? '🔄 Connecting to live stream...' :
                             isPlaying ? '🎵 Live Audio Stream Active' :
                             '⏱️ Ready to connect'}
                          </span>
                        </div>
                        
                        {/* Stream Quality Indicator */}
                        <div className="flex items-center space-x-1 text-xs text-gray-300">
                          <span>Stream:</span>
                          <span className="text-green-300 font-mono">MP3/AAC</span>
                        </div>
                      </div>
                    </div>

                    {/* Guaranteed Play Button */}
                    <div className="mb-4">
                      <button
                        onClick={() => {
                          window.open(RADIO_STREAM_URL, '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes,toolbar=yes,menubar=yes');
                        }}
                        className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-400 hover:to-blue-400 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
                      >
                        <div className="flex items-center justify-center space-x-3">
                          <span className="text-2xl">🎵</span>
                          <div className="text-left">
                            <div className="text-lg">GUARANTEED PLAY</div>
                            <div className="text-xs opacity-90">Opens radio stream in new window</div>
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Alternative Connection Methods */}
                    <div className="grid grid-cols-3 gap-3">
                      {/* Direct Stream Link */}
                      <button
                        onClick={() => {
                          window.open(RADIO_STREAM_URL, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
                          setIsPlaying(true);
                        }}
                        className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 rounded-lg p-3 text-center transition-colors"
                      >
                        <div className="text-blue-300 text-lg mb-1">🌐</div>
                        <div className="text-white text-xs font-semibold">External Player</div>
                      </button>

                      {/* Force Iframe Player */}
                      <button
                        onClick={forceIframe}
                        className="bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 rounded-lg p-3 text-center transition-colors"
                      >
                        <div className="text-purple-300 text-lg mb-1">🔧</div>
                        <div className="text-white text-xs font-semibold">Alternative Mode</div>
                      </button>

                      {/* Refresh Connection */}
                      <button
                        onClick={() => window.location.reload()}
                        className="bg-green-500/20 hover:bg-green-500/30 border border-green-400/30 rounded-lg p-3 text-center transition-colors"
                      >
                        <div className="text-green-300 text-lg mb-1">🔄</div>
                        <div className="text-white text-xs font-semibold">Refresh</div>
                      </button>
                    </div>
                  </div>

                  {/* Advanced Fallback: Iframe Player */}
                  {useIframe && (
                    <div className="mt-6 p-4 bg-black/30 rounded-lg">
                      <div className="text-white text-sm mb-3 text-center">
                        <span className="text-blue-300">🔧 Alternative Player Mode Active</span>
                      </div>
                      <div className="space-y-3">
                        <iframe
                          src={RADIO_STREAM_URL}
                          width="100%"
                          height="120"
                          frameBorder="0"
                          scrolling="no"
                          className="rounded-lg border border-white/10 bg-black/20"
                          title="Kioo Radio Stream Player"
                          allow="autoplay; media"
                          sandbox="allow-scripts allow-same-origin allow-presentation"
                        ></iframe>
                        <div className="text-center">
                          <p className="text-green-300 text-sm">🎵 External radio player loaded</p>
                          <p className="text-gray-400 text-xs">If no audio, try the External Player button above</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Browser Audio Controls (Ultimate Fallback) */}
                  <div className="mt-4 p-4 bg-black/20 rounded-lg">
                    <div className="text-gray-300 text-xs mb-2 text-center">Direct Browser Audio Controls</div>
                    <audio 
                      controls 
                      preload="none"
                      className="w-full h-12"
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                      crossOrigin="anonymous"
                    >
                      <source src={RADIO_STREAM_URL} type="audio/mpeg" />
                      <source src={RADIO_STREAM_URL} type="audio/mp3" />
                      <source src={RADIO_STREAM_URL} type="audio/aac" />
                      <p className="text-white text-xs">Your browser does not support audio streaming.</p>
                    </audio>
                    <div className="text-center mt-2">
                      <p className="text-gray-400 text-xs">👆 Click play button above to start streaming</p>
                      <p className="text-gray-500 text-xs">Stream URL: {RADIO_STREAM_URL}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alternative Access Methods */}
              <div className="grid grid-cols-2 gap-4 w-full max-w-md">
                <a
                  href={RADIO_STREAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-opacity-20 transition-colors"
                >
                  <div className="text-2xl mb-2">🌐</div>
                  <div className="text-white text-sm font-semibold">Web Player</div>
                  <div className="text-green-200 text-xs">Direct Link</div>
                </a>
                
                <a
                  href={RADIO_STREAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 text-center hover:bg-opacity-20 transition-colors block"
                >
                  <div className="text-2xl mb-2">💎</div>
                  <div className="text-white text-sm font-semibold">High Quality</div>
                  <div className="text-green-200 text-xs">Best Audio</div>
                </a>
              </div>

              {/* Share Buttons */}
              <div className="flex items-center space-x-4 pt-4">
                <span className="text-green-200 text-sm">Share:</span>
                <button 
                  onClick={() => navigator.share && navigator.share({
                    title: 'Kioo Radio 98.1 FM',
                    text: 'Listen to Kioo Radio - The Gift of Good News',
                    url: window.location.href
                  })}
                  className="text-white hover:text-green-200 transition-colors"
                >
                  📱 Share
                </button>
                <a
                  href={`https://wa.me/?text=Listen%20to%20Kioo%20Radio%2098.1%20FM%20-%20The%20Gift%20of%20Good%20News%20${window.location.origin}/listen-live`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-green-200 transition-colors"
                >
                  💬 WhatsApp
                </a>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }}
                  className="text-white hover:text-green-200 transition-colors"
                >
                  🔗 Copy Link
                </button>
              </div>
            </div>
          </div>

          {/* Alternative Listening Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-3xl mb-3">📻</div>
              <h3 className="text-white font-semibold mb-2">FM Radio</h3>
              <p className="text-green-100 text-sm">Tune in to 98.1 FM on your radio</p>
            </div>
            
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-3xl mb-3">💬</div>
              <h3 className="text-white font-semibold mb-2">WhatsApp</h3>
              <p className="text-green-100 text-sm">Listen via WhatsApp broadcast</p>
            </div>
            
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-3xl mb-3">📞</div>
              <h3 className="text-white font-semibold mb-2">Call In</h3>
              <div className="text-green-100 text-sm space-y-0.5">
                <div>🇱🇷 +231 777 981 981 · +231 887 981 981</div>
                <div>🇸🇱 +232 90 061 389</div>
                <div>🇬🇳 +224 626 718 421</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Programs */}
      {upcomingPrograms.length > 0 && (
        <section className="py-16 bg-white bg-opacity-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">
              📅 Coming Up Today
            </h2>
            
            <div className="space-y-4">
              {upcomingPrograms.map((program, index) => (
                <div
                  key={program.id}
                  className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 flex items-center justify-between border border-white border-opacity-20"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{program.title}</h3>
                      <p className="text-green-100 text-sm">
                        {program.host} • {program.language} • {program.category}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">{program.start_time}</p>
                    <p className="text-green-200 text-sm">{program.duration_minutes} min</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="/programs"
                className="inline-flex items-center space-x-2 text-white hover:text-green-200 transition-colors"
              >
                <span>View Full Schedule</span>
                <span>→</span>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Technical Information */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              📡 Technical Information
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-white font-semibold mb-4">Broadcast Details</h3>
                <ul className="space-y-2 text-green-100">
                  <li>📻 Frequency: 98.1 FM</li>
                  <li>📍 Location: Betche Hill, Foya, Lofa County, Liberia</li>
                  <li>📡 Range: 150+ mile radius</li>
                  <li>🌍 Coverage: Liberia, Sierra Leone, Guinea</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-white font-semibold mb-4">Streaming Options</h3>
                <ul className="space-y-2 text-green-100">
                  <li>💻 Web Player: High quality AAC stream</li>
                  <li>📱 Mobile: Optimized for low-data usage</li>
                  <li>📶 Minimum Speed: 56kbps recommended</li>
                  <li>🔄 Backup: Multiple server locations</li>
                  <li>⚡ Uptime: 99.9% availability target</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 p-4 bg-yellow-500 bg-opacity-20 rounded-lg border border-yellow-400 border-opacity-30">
              <div className="flex items-start space-x-3">
                <span className="text-yellow-300">⚠️</span>
                <div>
                  <h4 className="text-yellow-200 font-semibold mb-1">Low Data Mode</h4>
                  <p className="text-yellow-100 text-sm">
                    Experiencing data limitations? Enable low data mode for reduced bandwidth consumption 
                    while maintaining clear audio quality.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ListenLive;